import { MedplumClient } from "@medplum/core";
import { ValueSet } from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

/**
 * Finds the latest combined ValueSet, handling duplicates by keeping the most recent
 */
export async function findLatestCombinedValueSet(
  medplum: MedplumClient,
  valueSetUrl: string,
  organizationReference: string,
): Promise<ValueSet | undefined> {
  // First try by URL only (most reliable)
  let existing = await medplum.searchOne("ValueSet", { url: valueSetUrl });

  // Then try with compartment
  if (!existing) {
    existing = await medplum.searchOne("ValueSet", {
      url: valueSetUrl,
      _compartment: organizationReference,
    });
  }

  if (existing) {
    return existing;
  }

  // Finally search all ValueSets and deduplicate
  const all = await medplum.searchResources("ValueSet", { url: valueSetUrl });
  if (!all.length) {
    return undefined;
  }

  const [latest, ...rest] = all.sort(
    (a, b) =>
      new Date(b.meta?.lastUpdated ?? 0).getTime() - new Date(a.meta?.lastUpdated ?? 0).getTime(),
  );

  // Best-effort duplicate cleanup
  for (const duplicateValueSet of rest) {
    if (duplicateValueSet.id) {
      try {
        await medplum.deleteResource("ValueSet", duplicateValueSet.id);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: "combined-procedure-codes" },
          extra: {
            operation: "delete-duplicate-value-set",
            valueSetId: duplicateValueSet.id,
          },
        });
      }
    }
  }

  return latest;
}
